import React from "react";
import Link from "next/link";
import { Icon } from "../Icon";
import type { IconName } from "@/types/icons";
import { cva, type VariantProps } from "class-variance-authority";
import { LinkData } from "@/types";

export { IconButton, type IconButtonProps } from "./IconButton";

const buttonVariants = cva(
  "px-md py-sm rounded-4xl transition-all duration-200 inline-flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer outline-offset-0 focus-visible:outline-2 focus-visible:outline-offset-2 font-weight-medium",
  {
    variants: {
      variant: {
        primary: "",
        secondary: "border-2",
        tertiary: "",
      },
      color: {
        yellow: "outline-yellow",
        purple: "outline-purple",
      },
    },
    compoundVariants: [
      // Primary + Yellow
      {
        variant: "primary",
        color: "yellow",
        class: "bg-yellow text-black hover:bg-white hover:text-black",
      },
      // Primary + Purple
      {
        variant: "primary",
        color: "purple",
        class: "bg-purple text-white hover:bg-purple-dark",
      },
      // Secondary + Yellow
      {
        variant: "secondary",
        color: "yellow",
        class:
          "bg-transparent border-yellow text-yellow hover:border-white hover:text-white",
      },
      // Secondary + Purple
      {
        variant: "secondary",
        color: "purple",
        class:
          "bg-transparent border-purple text-purple hover:border-purple-dark hover:text-purple-dark",
      },
    ],
    defaultVariants: {
      variant: "primary",
      color: "yellow",
    },
  }
);

interface ButtonProps extends React.AriaAttributes {
  id?: string;
  role?: React.AriaRole;
  tabIndex?: number;
  icon?: IconName;
  label: string | React.ReactNode;
  link?: LinkData;
  className?: string;
  iconClassName?: string;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
  isDummy?: boolean;
  variant?: "primary" | "secondary" | "tertiary";
  color?: "yellow" | "purple";
  onClick?: (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => void;
}

type ButtonElement = HTMLButtonElement | HTMLAnchorElement | HTMLSpanElement;

export const Button = React.forwardRef<ButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      color = "yellow",
      icon,
      label,
      link,
      className = "",
      iconClassName = "",
      disabled = false,
      type = "button",
      isDummy = false,
      id,
      role,
      tabIndex,
      onClick,
      ...ariaProps
    },
    ref
  ) => {
    const content = (
      <>
        {icon && (
          <Icon
            name={icon}
            size={18}
            decorative
            aria-hidden="true"
            className={"inline mr-2 " + iconClassName}
          />
        )}
        {label}
      </>
    );

    if (isDummy) {
      return (
        <span
          ref={ref as React.Ref<HTMLSpanElement>}
          id={id}
          role={role}
          tabIndex={tabIndex}
          {...ariaProps}
          className={buttonVariants({ variant, color, className })}
          onClick={onClick}
        >
          {content}
        </span>
      );
    }

    if (link) {
      return (
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          id={id}
          href={link.href}
          className={buttonVariants({ variant, color, className })}
          target={link.target}
          rel={link.target === "_blank" ? "noopener noreferrer" : undefined}
          tabIndex={tabIndex}
          {...ariaProps}
          aria-label={link.ariaLabel}
          onClick={onClick}
          role={role ?? "button"}
        >
          {content}
        </Link>
      );
    }

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        id={id}
        type={type}
        className={buttonVariants({ variant, color, className })}
        disabled={disabled}
        role={role}
        tabIndex={tabIndex}
        {...ariaProps}
        onClick={onClick}
      >
        {content}
      </button>
    );
  }
);
Button.displayName = "Button";
